import { Checkbox, Input, Tooltip } from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormComponentProps } from '../../../../common';
import RoomPicker from '../../../../Components/Rooms/RoomPicker';
import { useRooms } from '../../../../hooks/useRooms';
import { EnrollmentInformationType } from '../../../../students/types';
import EnrollmentScheduleForm from '../EnrollmentScheduleForm';

type EnrollmentFormValues = {
  room: string;
  startDate: string;
  automaticallyEnroll?: boolean;
};

type NewEnrollmentInformationFormProps = FormComponentProps<EnrollmentInformationType> & {
  desiredStartDate?: string;
  desiredRoomId?: string;
  scheduledDays: any[];
  isSavingSchedule: boolean;
  onSaveSchedule: (params: any) => void;
  form: UseFormReturn<EnrollmentFormValues>;
};

function NewEnrollmentInformationForm({
  title,
  hasTitle = true,
  desiredStartDate,
  scheduledDays,
  isSavingSchedule,
  onSaveSchedule,
  form,
  desiredRoomId,
}: NewEnrollmentInformationFormProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = form;
  const rooms = useRooms();
  const hasAutoSelectedRef = useRef(false);

  // Filter rooms the same way RoomPicker does (exclude archived)
  const availableRooms = useMemo(() => {
    if (!rooms?.length) return [];
    return rooms.filter((room) => !room?.isArchived);
  }, [rooms]);

  useEffect(() => {
    if (desiredStartDate) {
      setValue('startDate', desiredStartDate);
    }
  }, [desiredStartDate, setValue]);

  // Auto-select room when there's only one available (only once, and only if no room is selected)
  useEffect(() => {
    const currentRoomValue = getValues('room');
    // Only auto-select if: no room is currently selected, there's exactly one room, and we haven't already auto-selected
    if (!currentRoomValue && availableRooms.length === 1 && !hasAutoSelectedRef.current) {
      setValue('room', availableRooms[0].id, { shouldValidate: false });
      hasAutoSelectedRef.current = true;
    }
    // Reset the ref if rooms change and we no longer have exactly one room
    if (availableRooms.length !== 1) {
      hasAutoSelectedRef.current = false;
    }
  }, [availableRooms.length, setValue, getValues]);

  const viewTitle = hasTitle ? t(title || 'students.enrollmentInformationFormTitle') : '';

  return (
    <div className="enrollment-information-form">
      {!!viewTitle && <h2 className="mb-4 text-2xl font-bold">{viewTitle}</h2>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Controller
            name="room"
            control={control}
            rules={{ required: t('students.enrollment.validation.pleaseSelectARoom') }}
            render={({ field }) => (
              <RoomPicker
                {...field}
                defaultRoomId={desiredRoomId}
                required
                label={t('enrollments.fields.room')}
                placeholder={t('enrollments.fields.roomPlaceholder')}
                error={errors.room?.message}
                data-testid="room-picker-field"
                value={field.value as string}
              />
            )}
          />
        </div>
        <div>
          <Controller
            name="startDate"
            control={control}
            rules={{
              required: t('enrollments.enrollmentInfoStartDateRequired'),
              validate: {
                maxDate: (date) => dayjs(date).isBefore(dayjs('2100-12-31')) || t('enrollments.errors.maxDate'),
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                required
                value={field.value ?? ''}
                label={t('enrollments.enrollmentInfoStartDateLabel')}
                type="date"
                inputType="date"
                min={dayjs().subtract(1, 'year').format('YYYY-MM-DD')}
                error={errors.startDate?.message}
              />
            )}
          />
        </div>
        <div></div>
        <div className="flex flex-wrap items-center">
          <Controller
            name="automaticallyEnroll"
            control={control}
            render={({ field }) => (
              <Checkbox
                {...field}
                name={field.name}
                wrapperClasses="flex items-center"
                label={t('students.automaticallyEnrollLabel')}
                data-testid="enroll-automatically"
                checked={field.value}
              />
            )}
          />
          <Tooltip action="hover" content={t('students.automaticallyEnrollLabelTooltipContent')}>
            <span className="ml-2 cursor-pointer whitespace-normal text-blue-900">
              {t('students.automaticallyEnrollLabelTooltipTrigger')}
            </span>
          </Tooltip>
        </div>
      </div>
      <div className="mt-6">
        <EnrollmentScheduleForm
          scheduledDays={scheduledDays}
          isSaving={isSavingSchedule}
          onSave={onSaveSchedule}
          errors={errors as Record<string, string>}
        />
      </div>
    </div>
  );
}

export default NewEnrollmentInformationForm;
